import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export const GEOGRAPHY_API_VERSION = 'adders.io/v1alpha1';
export const POSTCODE_PATTERN =
  '^(GIR ?0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y])?)|[0-9][A-HJKPS-UW]) ?[0-9][ABD-HJLNP-UW-Z]{2})$';
export const LOCATION_TYPES = [
  'populatedPlace',
  'districtBorough',
  'countyUnitary',
  'localAuthority',
  'administrativeCounty',
  'region',
  'country',
  'postcodeSector',
  'postcodeDistrict',
  'postcodeArea',
] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];
export const ADMIN_REF_FIELDS = [
  'localAuthorityRef',
  'countyRef',
  'regionRef',
  'countryRef',
] as const;
export const POSTAL_REF_FIELDS = [
  'postcodeSectorRef',
  'postcodeDistrictRef',
  'postcodeAreaRef',
] as const;
export const GEOGRAPHY_REF_FIELDS = [
  'settlementRef',
  ...ADMIN_REF_FIELDS,
  ...POSTAL_REF_FIELDS,
] as const;
export type GeographyRefField = (typeof GEOGRAPHY_REF_FIELDS)[number];
export type GeographyRefs = Partial<Record<GeographyRefField, string>>;
export type PostcodeEntity = Entity & {
  spec: GeographyRefs & {
    postcode: string;
    status?: 'live' | 'terminated';
    introduced?: string;
    terminated?: string;
    latLng?: { lat: number; lng: number };
    bng?: { x: number; y: number };
  };
};
export type PlaceEntity = Entity & {
  spec: { type: LocationType; name: string; parentRefs?: string[] };
};
export interface PostcodeLookupData {
  postcode: string;
  city?: string;
  districtBorough?: string;
  county?: string;
  region?: string;
  country?: string;
  coordinates?: { x: number; y: number };
  latitude?: number;
  longitude?: number;
  isTerminated: boolean;
}
export interface PostcodeLookupResult {
  entity: PostcodeEntity;
  locations: Entity[];
  data: PostcodeLookupData;
}

export function normalizePostcode(value: string): string {
  return value.toLowerCase().replace(/\s+/g, '');
}
export function canonicalPostcode(value: string): string {
  const key = normalizePostcode(value).toUpperCase();
  if (!new RegExp(POSTCODE_PATTERN).test(key))
    throw new Error(`Invalid postcode: ${value}`);
  return `${key.slice(0, -3)} ${key.slice(-3)}`;
}
export function geographyRef(value: string): string {
  if (
    !/^(postcode|postcodelocation):[a-z0-9_.-]+\/[a-z0-9_.-]+$/i.test(value)
  ) {
    throw new Error(
      `Expected a full postcode or postcodelocation entity reference: ${value}`,
    );
  }
  return stringifyEntityRef(parseEntityRef(value));
}
export function outgoingGeographyRefs(entity: Entity): string[] {
  const spec = entity.spec ?? {};
  const refs: unknown[] = GEOGRAPHY_REF_FIELDS.map(field => spec[field]);
  if (Array.isArray(spec.parentRefs)) refs.push(...spec.parentRefs);
  return [
    ...new Set(
      refs
        .filter((ref): ref is string => typeof ref === 'string')
        .map(geographyRef),
    ),
  ];
}
export function isGeography(entity: Entity): boolean {
  return ['postcode', 'postcodelocation'].includes(entity.kind.toLowerCase());
}

export const geographyRefProperties = Object.fromEntries(
  GEOGRAPHY_REF_FIELDS.map(field => [
    field,
    {
      type: 'string',
      pattern: '^postcodelocation:[a-zA-Z0-9_.-]+/[a-zA-Z0-9_.-]+$',
    },
  ]),
);
